import { storeToRefs } from 'pinia'

import { useMainStore } from '@/stores/main'
import { openExternalUrl } from '@/utils/url'

const DEFAULT_PORTS = { 'https:': '443', 'http:': '80' }

// 'self' 指目標站自身的 origin；本站對目標站必為跨源，故與 'none' 同樣視為不可嵌入
const isSourceMatchingCurrentOrigin = (source) => {
  if (source === 'none' || source === 'self') return false
  if (source === '*') return true
  if (/^[a-z][a-z0-9+.-]*:$/.test(source)) return source === window.location.protocol
  const schemeMatch = source.match(/^([a-z][a-z0-9+.-]*):\/\//)
  if (schemeMatch && `${schemeMatch[1]}:` !== window.location.protocol) return false
  const [host, port] = source
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .split('/')[0]
    .split(':')
  const currentPort = window.location.port || DEFAULT_PORTS[window.location.protocol] || ''
  if (port && port !== '*' && port !== currentPort) return false
  if (host === window.location.hostname) return true
  return host.startsWith('*.') && window.location.hostname.endsWith(host.slice(1))
}

// frame-ancestors 只要存在，就必須明確允許本站 origin，否則瀏覽器會阻擋嵌入（'self'、不含本站的 allowlist 都會擋）
const isFrameAncestorsAllowingCurrentOrigin = (csp) => {
  const directive = csp
    .split(/[;,]/)
    .map((part) => part.trim())
    .find((part) => /^frame-ancestors(\s|$)/i.test(part))
  if (!directive) return true
  const sources = directive.split(/\s+/).slice(1)
  if (!sources.length) return false
  return sources.some((rawSource) => isSourceMatchingCurrentOrigin(rawSource.replace(/^'+|'+$/g, '').toLowerCase()))
}

// 三方社群、金流、遊戲 type=4 等連結通常設有 X-Frame-Options/CSP 禁止被 iframe 嵌入，
// 必須直接用 openExternalUrl 開啟，不走 openWithIframeFallback 的檢測流程。
export default defineNuxtPlugin(() => {
  const mainStore = useMainStore()
  const { isIOS, isInStandaloneMode } = storeToRefs(mainStore)

  let isProbing = false

  const openWithIframeFallback = async (url) => {
    // iOS webClip 嘗試使用 iframe 開啟，禁止或失敗則 openExternalUrl。其餘裝置情況直接 openExternalUrl。
    if (!(isIOS.value && isInStandaloneMode.value)) {
      openExternalUrl(url)
      return
    }

    if (isProbing) {
      openExternalUrl(url)
      return
    }
    isProbing = true

    try {
      // 同源 URL 不走 iframe，避免 allow-scripts + allow-same-origin sandbox escape
      try {
        if (new URL(url).origin === window.location.origin) {
          openExternalUrl(url)
          return
        }
      } catch {
        openExternalUrl(url)
        return
      }

      const resp = await fetch(url, {
        method: 'HEAD',
        mode: 'cors',
        credentials: 'omit',
        signal: AbortSignal.timeout(3000),
      })
      if (!resp.ok) {
        openExternalUrl(url)
        return
      }
      const xfo = resp.headers.get('x-frame-options')
      if (xfo && /^(deny|sameorigin)$/i.test(xfo.trim())) {
        openExternalUrl(url)
        return
      }
      // CSP frame-ancestors 須明確允許本站才嵌入（僅在 CORS 允許讀取 header 時有效）
      const csp = resp.headers.get('content-security-policy')
      if (csp && !isFrameAncestorsAllowingCurrentOrigin(csp)) {
        openExternalUrl(url)
        return
      }
      mainStore.setIframeUrl(url)
    } catch {
      openExternalUrl(url)
    } finally {
      isProbing = false
    }
  }

  return {
    provide: {
      openWithIframeFallback,
    },
  }
})
